import React, { useRef, useState } from "react";
import { DatePicker } from "./ui/date-picker";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogClose,
} from "./ui/dialog";
import { Label } from "./ui/label";
import { Input } from "./ui/input";
import { FaEye, FaEyeSlash } from "react-icons/fa6";
import SuperAdminService from "../services/superAdmin";
import { toastError, toastSuccess } from "./ToastNotification";
import { Camera, Pencil } from "lucide-react";
import Loader from "../assets/images/loader.gif";
import User from "../assets/images/user.png";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Checkbox } from "./ui/checkbox";

const AddAdmin = ({ type, getAllAdmin, data }) => {

  const orgSubscription = data?.orgSubscription
  console.log("data", orgSubscription)
  const [showPassword, setShowPassword] = useState(false);
  const [isOpen, setIsOpen] = useState(false); // State for modal visibility
  const [userProfile, setUserProfile] = useState("");
  const fileInputRef = useRef(null);
  const [image, setImage] = useState(User);
  const [isLoading, setIsLoading] = useState(false);
  // Prevent multiple fast clicks from sending multiple requests concurrently.
  const isSubmittingRef = useRef(false);

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  const refreshBothAdminLists = async () => {
    if (!getAllAdmin) return;
    await getAllAdmin();
  };

  const handleSubmit = async (values) => {
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    setIsLoading(true);
    const formData = new FormData();
    formData.append("name", values.name);
    formData.append("email", values.email);
    formData.append("mobileNumber", values.mobileNumber);

    if (userProfile) {
      formData.append("profile", userProfile);
    }

    if (values.current_period_start || (data?.orgSubscription === undefined || data?.orgSubscription?.subscription_type === "manual")) {
      formData.append("current_period_start", values.current_period_start);
    }
    if (values.current_period_end || (data?.orgSubscription === undefined || data?.orgSubscription?.subscription_type === "manual")) {
      formData.append("current_period_end", values.current_period_end);
    }
    if (values.purchased_seats || (data?.orgSubscription === undefined || data?.orgSubscription?.subscription_type === "manual")) {
      formData.append("purchased_seats", values.purchased_seats);
    }
    if (values.organization_id || (data?.orgSubscription === undefined || data?.orgSubscription?.subscription_type === "manual")) {
      formData.append("organization_id", values.organization_id);
    }
    // if (values.is_active !== undefined || (data?.orgSubscription === undefined || data?.orgSubscription?.subscription_type === "manual")) {
    //   formData.append("is_active", values.is_active);
    // }

    if (type === "add") {
      formData.append("userType", values.userType === "Organization" ? "OrganizationAdmin" : "IndividualAdmin");
      if (values.userType === "Organization") {
        formData.append("companyName", values.companyName);
      }
      formData.append("password", values.password);
    }

    if (type === "add") {
      try {
        const response = await SuperAdminService?.createAdmin(formData);
        if (response) {
          toastSuccess(response?.data?.message);
          await refreshBothAdminLists();
          setIsOpen(false); // Close modal on successful submission
          formik.resetForm(); // Reset form fields
        }
      } catch (error) {
        toastError(error?.response?.data?.message);
      } finally {
        setIsLoading(false);
        isSubmittingRef.current = false;
      }
    } else {
      try {
        console.log("formadata", formData)
        // const response = "ok"
        const response = await SuperAdminService?.updateAdmin(
          data._id,
          formData
        );
        if (response) {
          toastSuccess(response?.data?.message);
          await refreshBothAdminLists();
          setIsOpen(false); // Close the modal after successful submission
        }
      } catch (error) {
        toastError(error?.response?.data?.message);
      } finally {
        formik.resetForm();
        setIsLoading(false);
        isSubmittingRef.current = false;
      }
      // Handle update logic here
    }
  };

  // Formik setup
  const formik = useFormik({
    initialValues: {
      name: "",
      companyName: "",
      email: "",
      mobileNumber: "",
      password: "",
      userType: "",
      profile: userProfile,
      organization_id: "",
      purchased_seats: "",
      current_period_start: "",
      current_period_end: "",
      is_active: true,
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Name is required"),
      // companyName: Yup.string()
      //   .required("Company name is required")
      //   .min(3, "Company name must be at least 3 characters long")
      //   .max(63, "Company name must be at most 63 characters long")
      //   .matches(
      //     /^[a-zA-Z0-9](?!.*[.]{2})[a-zA-Z0-9._-]*[a-zA-Z0-9]$/,
      //     "Company name must start and end with an alphanumeric character, and contain only alphanumeric characters, underscores, or hyphens."
      //   ),
      email: Yup.string()
        .email("Invalid email address")
        .required("Email is required"),
      mobileNumber: Yup.string().required("Number is required"),
      password:
        type === "add" &&
        Yup.string()
          .required("Password is required")
          .min(8, "Password must be at least 8 characters"),
      userType:
        type === "add" && Yup.string().required("User type is required"),
      companyName: Yup.string().when("userType", {
        is: "Organization",
        then: () => Yup.string().required("Company name is required"),
        otherwise: () => Yup.string().notRequired(),
      }),
      current_period_start: (data?.orgSubscription === undefined || data?.orgSubscription?.subscription_type === "manual") && Yup.date().nullable(),
      current_period_end: (data?.orgSubscription === undefined || data?.orgSubscription?.subscription_type === "manual") && Yup.date()
        .min(Yup.ref("current_period_start"), "End date must be after start date")
        .nullable(),
      purchased_seats: (data?.orgSubscription === undefined || data?.orgSubscription.subscription_type === "manual") && Yup.number()
        .nullable()
        .min(1000, "Number of users must be at least 1000"),
      is_active: (data?.orgSubscription === undefined || data?.orgSubscription.subscription_type === "manual") && Yup.boolean().nullable(),
    }),
    onSubmit: handleSubmit,
  });

  const handleImageClick = () => {
    fileInputRef.current.click();
  };

  const handleImageChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setUserProfile(file);
      const reader = new FileReader();
      reader.onload = (e) => setImage(e.target.result);
      reader.readAsDataURL(file);
    }
  };

  const handleModalOpen = async (type) => {
    setIsOpen(true);
    if (type === "add") {
      setImage(User);
      formik.resetForm();
    } else if (type === "edit") {
      setImage(data.profile || User);
      formik.setFieldValue("email", data.email);
      formik.setFieldValue("mobileNumber", data.mobileNumber);
      formik.setFieldValue("name", data.name);
      formik.setFieldValue("userType", data.userType || (data.company?.name ? "Organization" : "Individual"));
      formik.setFieldValue("companyName", data.company?.name || "");
      formik.setFieldValue("profile", data.profile);
      formik.setFieldValue(
        "current_period_start",
        data.orgSubscription?.current_period_start
          ? new Date(data.orgSubscription.current_period_start).toISOString().split("T")[0]
          : ""
      );
      formik.setFieldValue(
        "current_period_end",
        data.orgSubscription?.current_period_end
          ? new Date(data.orgSubscription.current_period_end).toISOString().split("T")[0]
          : ""
      );
      formik.setFieldValue(
        "purchased_seats",
        data.orgSubscription?.purchased_seats || ""
      );
      formik.setFieldValue(
        "organization_id",
        data.orgSubscription?.organization_id || data._id || ""
      );
      formik.setFieldValue("is_active", data.orgSubscription?.is_active === true);
    }
  };

  const handleTypChange = (value) => {
    formik.setFieldValue("userType", value);
    formik.setFieldTouched("userType", true);
  };

  // const handleImageClick = () => {
  //   fileInputRef.current.click();
  // };

  // const handleImageChange = (event) => {
  //   const file = event.target.files[0];
  //   if (file) {
  //     setUserProfile(file);
  //     const reader = new FileReader();
  //     reader.onload = (e) => setImage(e.target.result);
  //     reader.readAsDataURL(file);
  //   }
  // };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        {type === "add" ? (
          <Button className="bg-primary hover:bg-btnHover text-white text-base">
            + Add New
          </Button>
        ) : (
          <div
            className="group border rounded-md p-1 border-[#7064E9] hover:bg-[#7064E9] duration-200 cursor-pointer"
            onClick={() => handleModalOpen("edit")}
          >
            <Pencil className="w-4 h-4 text-[#7064E9] group-hover:text-white" />
          </div>
        )}
      </DialogTrigger>
      <DialogContent
        className="sm:max-w-[500px] max-w-[calc(100%-40px)] mx-auto bg-sidebar max-h-[calc(100vh-10vh)] overflow-y-auto custom-scroll"
        onInteractOutside={(event) => event.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="text-white">{`${type === "add" ? "Add Admin" : "Edit Admin"
            }`}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={formik.handleSubmit}>
          <div className="grid gap-4 py-4">
            {/* Upload Image Code Comment */}
            <div className="flex justify-center">
              <div className="relative">
                <div className="h-20 w-20 rounded-full overflow-hidden border">
                  <img
                    src={image}
                    alt="User"
                    className="object-cover bg-center w-full h-full"
                  />
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  accept="image/png, image/jpeg"
                  onChange={handleImageChange}
                />
                <div
                  className="cursor-pointer absolute bottom-0 right-0 bg-secondary border rounded-full p-1"
                  onClick={handleImageClick}
                >
                  <Camera className="h-4 w-4" />
                </div>
              </div>
            </div>
            {/* Name Field */}
            <div className="grid items-center gap-1.5">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                placeholder="Name"
                {...formik.getFieldProps("name")}
              />
              {formik.touched.name && formik.errors.name ? (
                <div className="text-red-500 text-sm">{formik.errors.name}</div>
              ) : null}
            </div>


            {/* Email Field */}
            <div className="grid items-center gap-1.5">
              <Label htmlFor="email">Email</Label>
              <Input
                type="email"
                id="email"
                placeholder="Email"
                {...formik.getFieldProps("email")}
              />
              {formik.touched.email && formik.errors.email ? (
                <div className="text-red-500 text-sm">
                  {formik.errors.email}
                </div>
              ) : null}
            </div>

            {/* Mobile Number Field */}
            <div className="grid items-center gap-1.5">
              <Label htmlFor="mobileNumber">Phone Number</Label>
              <Input
                id="mobileNumber"
                type="number"
                placeholder="Phone Number"
                {...formik.getFieldProps("mobileNumber")}
              />
              {formik.touched.mobileNumber && formik.errors.mobileNumber ? (
                <div className="text-red-500 text-sm">
                  {formik.errors.mobileNumber}
                </div>
              ) : null}
            </div>

            {type === "add" && (
              <>
                <div className="grid items-center gap-1.5">
                  <Label htmlFor="userType">User Type</Label>
                  <Select
                    onValueChange={handleTypChange}
                    value={formik.values.userType}
                  >
                    <SelectTrigger
                      name="userType"
                      id="userType"
                      onBlur={formik.handleBlur}
                      className="w-full"
                      tabIndex="-1"
                    >
                      <SelectValue placeholder="Select a user type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectGroup>
                        <SelectItem value="Individual">Individual</SelectItem>
                        <SelectItem value="Organization">
                          Organization
                        </SelectItem>
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                  {/* {(formik.touched.userType || formik.submitCount > 0) &&
                    formik.errors.userType ? (
                    <div className="text-red-500 text-sm">
                      {formik.errors.userType}
                    </div>
                  ) : null} */}
                </div>
              </>
            )}

            {/* Conditional Company Field */}
            {formik.values.userType === "Organization" && (
              <div className="grid items-center gap-1.5">
                <Label htmlFor="companyName">Company Name</Label>
                <Input
                  id="companyName"
                  placeholder="Company Name"
                  {...formik.getFieldProps("companyName")}
                />
                {formik.touched.companyName && formik.errors.companyName ? (
                  <div className="text-red-500 text-sm">
                    {formik.errors.companyName}
                  </div>
                ) : null}
              </div>
            )}

            {type === "add" && (
              <>
                {/* Password Field */}
                <div className="grid items-center gap-1.5">
                  <Label htmlFor="password">Password</Label>
                  <div className="relative">
                    <Input
                      type={showPassword ? "text" : "password"}
                      id="password"
                      placeholder="Password"
                      {...formik.getFieldProps("password")}
                    />
                    <div
                      className="absolute top-1/2 right-4 -translate-y-1/2 cursor-pointer"
                      onClick={togglePasswordVisibility}
                    >
                      {showPassword ? (
                        <FaEyeSlash className="text-white/50" />
                      ) : (
                        <FaEye className="text-white/50" />
                      )}
                    </div>
                  </div>
                  {formik.touched.password && formik.errors.password ? (
                    <div className="text-red-500 text-sm">
                      {formik.errors.password}
                    </div>
                  ) : null}
                </div>
              </>
            )}

            {type === "edit" && formik.values.userType === "Organization" && (data.orgSubscription === undefined || data.orgSubscription.subscription_type === "manual") && (
              <>
                <div className="mt-2">
                  <Label className="text-base font-semibold border-b border-white/10 pb-1 block text-primary">
                    Subscription
                  </Label>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="grid items-center gap-1.5">
                    <Label htmlFor="purchased_seats">Users</Label>
                    <Input
                      id="purchased_seats"
                      type="number"
                      placeholder="Purchased Seats"
                      {...formik.getFieldProps("purchased_seats")}
                    />
                    {formik.touched.purchased_seats &&
                      formik.errors.purchased_seats ? (
                      <div className="text-red-500 text-sm">
                        {formik.errors.purchased_seats}
                      </div>
                    ) : null}
                  </div>

                  {/* <div className="flex items-center gap-2 pt-6"> */}
                  {/* <Checkbox */}
                  {/* id="is_active" */}
                  {/* checked={formik.values.is_active} */}
                  {/* onCheckedChange={(checked) => */}
                  {/* formik.setFieldValue("is_active", checked) */}
                  {/* } */}
                  {/* /> */}
                  {/* <Label htmlFor="is_active" className="cursor-pointer"> */}
                  {/* Is Active */}
                  {/* </Label> */}
                  {/* </div> */}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="grid items-center gap-2">
                    <Label htmlFor="current_period_start">Start Date</Label>
                    <DatePicker
                      id="current_period_start"
                      value={formik.values.current_period_start}
                      onChange={(val) => formik.setFieldValue("current_period_start", val)}
                      placeholder="Pick start date"
                    />
                    {formik.touched.current_period_start &&
                      formik.errors.current_period_start ? (
                      <div className="text-red-500 text-sm">
                        {formik.errors.current_period_start}
                      </div>
                    ) : null}
                  </div>
                  <div className="grid items-center gap-2">
                    <Label htmlFor="current_period_end">End Date</Label>
                    <DatePicker
                      id="current_period_end"
                      value={formik.values.current_period_end}
                      onChange={(val) => formik.setFieldValue("current_period_end", val)}
                      placeholder="Pick end date"
                    />
                    {formik.touched.current_period_end &&
                      formik.errors.current_period_end ? (
                      <div className="text-red-500 text-sm">
                        {formik.errors.current_period_end}
                      </div>
                    ) : null}
                  </div>
                </div>
              </>
            )}
          </div>
          <DialogFooter className="flex gap-4">
            <DialogClose asChild>
              <Button
                type="button"
                variant="outline"
                className="w-full bg-transparent border text-primary border-primary hover:bg-primary hover:text-white"
                onClick={() => {
                  setIsOpen(false);
                  formik.resetForm(); // Reset form fields
                }}
              >
                Cancel
              </Button>
            </DialogClose>
            <Button
              type="submit"
              variant="destructive"
              className="w-full bg-primary hover:bg-btnHover text-white hover:text-white text-base border-0"
              disabled={isLoading}
            >   
              {isLoading ? (
                <img src={Loader} alt="Loader" className="w-7" />
              ) : (
                "Save"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddAdmin;
